// Top-level Orchestrator page — cross-Workspace macro cockpit.
//
// Captain canon 2026-04-29:
//   - "Operating" happens INSIDE a single Workspace (parallel
//     agents on worktrees still count as Operating). The Operator
//     tab in WorkspaceRoomPage is its surface.
//   - "Orchestrating" happens OUTSIDE any single Workspace — across
//     Workspaces, watching all of them at once. THIS page is its
//     surface.
//
// First-cut content uses the same SDB tables already subscribed
// to by the Sidebar/HomePage so we have a real surface to hang
// the new meta-harness ideas on (running-units, watcher
// intervention receipts, sub-vs-API cost comparison) without
// inventing fake state.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowRight } from "lucide-react";
import { SidebarShell } from "../layout/SidebarShell";
import { ApiError, extractRows, listOrgs, listOrgWorkspaces } from "../api/client";

interface OrgRow {
  org_id: number;
  slug: string;
  name: string;
}

interface WorkspaceRow {
  workspace_id: number;
  org_id: number;
  slug: string;
  name: string;
  daytona_state?: string;
  paused?: boolean;
}

function rowToOrg(row: unknown[]): OrgRow | null {
  const id = Number(row[0]);
  const slug = String(row[1] ?? "");
  const name = String(row[2] ?? "");
  if (!id || !slug) return null;
  return { org_id: id, slug, name };
}

function rowToWorkspace(row: unknown[]): WorkspaceRow | null {
  const id = Number(row[0]);
  const orgId = Number(row[1] ?? 0);
  const slug = String(row[2] ?? "");
  const name = String(row[3] ?? "");
  const sandboxState = String(row[7] ?? "cold") || "cold";
  const paused = row[10] === true || row[10] === "true";
  if (!id || !slug) return null;
  return {
    workspace_id: id,
    org_id: orgId,
    slug,
    name,
    daytona_state: sandboxState,
    paused,
  };
}

export function OrchestratorPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [workspacesByOrg, setWorkspacesByOrg] = useState<
    Map<number, WorkspaceRow[]>
  >(new Map());
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const orgsRaw = await listOrgs();
        const orgList = extractRows(orgsRaw)
          .map(rowToOrg)
          .filter((o): o is OrgRow => o !== null);
        if (cancelled) return;
        setOrgs(orgList);

        const next = new Map<number, WorkspaceRow[]>();
        await Promise.all(
          orgList.map(async (org) => {
            try {
              const wsRaw = await listOrgWorkspaces(org.org_id);
              const list = extractRows(wsRaw)
                .map(rowToWorkspace)
                .filter((w): w is WorkspaceRow => w !== null);
              next.set(org.org_id, list);
            } catch {
              next.set(org.org_id, []);
            }
          }),
        );
        if (!cancelled) setWorkspacesByOrg(next);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : "Could not load orchestrator state",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalWorkspaces = Array.from(workspacesByOrg.values()).reduce(
    (sum, list) => sum + list.length,
    0,
  );
  const liveWorkspaces = Array.from(workspacesByOrg.values())
    .flat()
    .filter((w) => w.daytona_state === "active" || w.daytona_state === "warming")
    .length;

  return (
    <SidebarShell>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-8">
        <header className="border-b border-edge pb-4">
          <p className="font-code text-[0.625rem] text-faint">Orchestrator</p>
          <h1 className="mt-1 text-xl font-semibold text-primary">
            Across every Workspace
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted">
            Macro cockpit. What lives here is anything that doesn't fit inside
            a single Workspace — every Workspace's running units, watcher
            interventions across them, cross-Workspace cost roll-up, shadow
            comparisons, and the bigger questions about how Waytide is
            performing as a system.
          </p>
        </header>

        <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Metric label="Orgs" value={String(orgs.length)} />
          <Metric label="Workspaces" value={String(totalWorkspaces)} />
          <Metric
            label="Live now"
            value={String(liveWorkspaces)}
            tone={liveWorkspaces > 0 ? "accent" : "muted"}
          />
          <Metric label="Watcher lanes" value="—" detail="A41 pending" />
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-primary">Workspaces</h2>
          {error && (
            <p className="text-xs text-danger" data-testid="orchestrator-error">
              {error}
            </p>
          )}
          {orgs.length === 0 && !error ? (
            <p className="text-xs text-muted">No orgs yet.</p>
          ) : (
            <div className="flex flex-col gap-4">
              {orgs.map((org) => {
                const list = workspacesByOrg.get(org.org_id) ?? [];
                return (
                  <div
                    key={org.org_id}
                    className="rounded-panel border border-edge bg-surface-glass p-4"
                  >
                    <header className="mb-2 flex items-baseline justify-between">
                      <h3 className="text-sm font-semibold text-primary">
                        {org.name}
                      </h3>
                      <span className="font-code text-[0.625rem] text-faint">
                        {list.length} workspace{list.length === 1 ? "" : "s"}
                      </span>
                    </header>
                    {list.length === 0 ? (
                      <p className="text-xs text-muted">
                        No Workspaces yet — create one from Home.
                      </p>
                    ) : (
                      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {list.map((w) => (
                          <li key={w.workspace_id}>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/workspaces/${w.workspace_id}`)
                              }
                              className="flex w-full items-center justify-between gap-2 rounded-md border border-edge bg-surface-raised px-3 py-2 text-left transition-colors hover:border-accent"
                              data-testid={`orchestrator-workspace-${w.slug}`}
                            >
                              <div className="min-w-0">
                                <p className="truncate text-xs font-medium text-primary">
                                  {w.name}
                                </p>
                                <p className="truncate font-code text-[0.625rem] text-faint">
                                  {w.daytona_state ?? "cold"}
                                  {w.paused ? " · paused" : ""}
                                </p>
                              </div>
                              <ArrowRight size={12} className="shrink-0 text-muted" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-panel border border-edge bg-surface-glass p-4">
          <header className="mb-2 flex items-center gap-2 text-xs font-semibold text-primary">
            <Activity size={12} className="text-accent" />
            Coming next (A41)
          </header>
          <ul className="flex flex-col gap-1.5 text-xs text-muted">
            <li>· Running-unit registry across every Workspace + lane.</li>
            <li>· Watcher intervention feed (focus, drift, research, visual review, triage).</li>
            <li>· Async sidecar queue with budgets and receipts.</li>
            <li>· Subscription-vs-API cost comparison across the same task tags.</li>
            <li>· Sentry / PostHog triage feeding watcher notes.</li>
            <li>· Knowledge-graph across docs, receipts, decisions, scores.</li>
          </ul>
        </section>
      </div>
    </SidebarShell>
  );
}

function Metric({
  label,
  value,
  detail,
  tone = "muted",
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: "accent" | "muted";
}) {
  return (
    <div className="rounded-md border border-edge bg-surface-glass px-3 py-2">
      <p className="font-code text-[0.625rem] text-faint">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold ${
          tone === "accent" ? "text-accent" : "text-primary"
        }`}
      >
        {value}
      </p>
      {detail && <p className="mt-0.5 text-[0.625rem] text-muted">{detail}</p>}
    </div>
  );
}
